import { createStore } from 'zustand';
import { UploadTaskStatus, UploadErrorCode, UploadTaskPriority } from '@fabric-app/types/upload';
import { subscribeWithSelector } from '../../node_modules/zustand/esm/middleware.js';
import { v4 } from 'uuid';
import { calculateChunkCount } from './utils/calculateChunkCount.js';
import { calculateChunkSize } from './utils/calculateChunkSize.js';
import { UploadError } from './upload.error.js';
import { createLogger } from '@fabric-app/utils/logging';
import { getCompatEnv } from '@fabric-app/compat/capabilities/environment';
import { throttle } from 'lodash-es';
import { isDefined } from '@fabric-app/utils/guards';

const uploadStore = createStore()(subscribeWithSelector((set, get) => {
  let isScheduling = false;
  let scheduleQueued = false;
  const schedule = async () => {
    if (isScheduling) {
      scheduleQueued = true;
      return;
    }
    try {
      isScheduling = true;
      let state = get();
      let uploadingTasks = state.tasks.filter((t) => t.status === UploadTaskStatus.Uploading);
      const pendingInstantTasks = state.tasks.filter((t) => t.status === UploadTaskStatus.Pending && isDefined(t.initialize) && t.priority === UploadTaskPriority.Instant);
      const pendingTasks = state.tasks.filter((t) => t.status === UploadTaskStatus.Pending && isDefined(t.initialize) && // instant tasks are handled above
      t.priority !== UploadTaskPriority.Instant).sort((a, b) => a.priority - b.priority || a.addedAt - b.addedAt).slice(0, Math.max(0, state.MAX_CONCURRENT_FILES - uploadingTasks.length - pendingInstantTasks.length));
      const tasksToUpload = [
        ...pendingInstantTasks,
        ...pendingTasks
      ];
      for (const task of tasksToUpload) {
        await task.initialize?.(task);
      }
      state = get();
      const uploadingChunks = state.tasks.filter((t) => t.status === UploadTaskStatus.Uploading).flatMap((t) => t.chunks.filter((c) => c.status === UploadTaskStatus.Uploading));
      const pendingChunks = state.tasks.filter((t) => t.status === UploadTaskStatus.Uploading).flatMap((task) => task.chunks.filter((c) => c.status === UploadTaskStatus.Pending || c.status === UploadTaskStatus.Failed && c.retries < state.MAX_CHUNK_RETRIES && c.error instanceof UploadError && c.error.retryable).map((chunk) => ({
        chunk,
        task
      }))).sort((a, b) => {
        if (b.task.priority !== a.task.priority) {
          return b.task.priority - a.task.priority;
        }
        return a.task.addedAt - b.task.addedAt || a.chunk.index - b.chunk.index;
      });
      let activeChunks = uploadingChunks.length;
      const newlyActiveChunks = {};
      const maxTotalChunks = state.MAX_CONCURRENT_CHUNKS_PER_FILE * state.MAX_CONCURRENT_FILES;
      for (const { chunk, task } of pendingChunks) {
        const activeTaskChunks = task.chunks.filter((c) => c.status === UploadTaskStatus.Uploading).length + (newlyActiveChunks[task.id] ?? 0);
        const maxConcurrentChunks = task.maxConcurrentChunksCount || state.MAX_CONCURRENT_CHUNKS_PER_FILE;
        if (activeTaskChunks >= maxConcurrentChunks) {
          continue;
        }
        if (activeChunks >= maxTotalChunks && task.priority !== UploadTaskPriority.Instant) {
          continue;
        }
        activeChunks++;
        if (newlyActiveChunks[task.id] === void 0) {
          newlyActiveChunks[task.id] = 1;
        } else {
          newlyActiveChunks[task.id]++;
        }
        chunk.execute?.(task, chunk);
      }
      state = get();
      uploadingTasks = state.tasks.filter((t) => t.status === UploadTaskStatus.Uploading);
      for (const task of uploadingTasks) {
        const erroredChunks = task.chunks.filter((c) => c.status === UploadTaskStatus.Failed);
        if (erroredChunks.length > 0) {
          get().failTask(task.id, erroredChunks[0].error ?? new UploadError(UploadErrorCode.Unknown));
        }
        if (task.chunks.every((c) => c.status === UploadTaskStatus.Completed)) {
          get().completeTask(task.id);
        }
      }
      state = get();
      state.tasks.forEach((task) => {
        if (task.status === UploadTaskStatus.Completed && isDefined(task.endTime) && Date.now() - task.endTime > state.SETTLED_TASK_EXPIRATION_MS) {
          get().removeTask(task.id);
        }
      });
    } finally {
      isScheduling = false;
      if (scheduleQueued) {
        scheduleQueued = false;
        setTimeout(() => get().schedule(), 0);
      }
    }
  };
  const throttledSchedule = throttle(schedule, 500, {
    leading: true,
    trailing: true
  });
  return {
    tasks: [],
    MAX_CONCURRENT_FILES: 3,
    MAX_CONCURRENT_CHUNKS_PER_FILE: 5,
    MAX_CHUNK_RETRIES: 5,
    SETTLED_TASK_EXPIRATION_MS: 1e3 * 60 * 5,
    schedule: throttledSchedule,
    addTask: (taskData, options) => {
      const id = v4();
      const disableChunking = options?.disableChunking ?? false;
      const chunkSize = disableChunking ? taskData.data.file.size : calculateChunkSize(taskData.data.file.size);
      const totalChunks = disableChunking ? 1 : calculateChunkCount(taskData.data.file.size);
      const lastChunkSize = disableChunking ? taskData.data.file.size : taskData.data.file.size - (totalChunks - 1) * chunkSize;
      const chunks = Array.from({
        length: totalChunks
      }, (_, i) => ({
        index: i,
        status: UploadTaskStatus.Pending,
        uploadSpeed: 0,
        progress: 0,
        size: i === totalChunks - 1 ? lastChunkSize : chunkSize,
        retries: 0
      }));
      const task = {
        ...taskData,
        id,
        addedAt: Date.now(),
        chunks,
        chunkSize,
        chunked: chunks.length > 1 && !disableChunking,
        status: UploadTaskStatus.Pending,
        progress: 0,
        uploadSpeed: 0,
        events: []
      };
      set((state) => ({
        tasks: [
          ...state.tasks,
          task
        ]
      }));
      get().schedule();
      return task;
    },
    updateTask: (taskId, update) => {
      const task = get().tasks.find((t) => t.id === taskId);
      if (!task) throw new UploadError(UploadErrorCode.TaskMissingResult);
      const updatedTask = update(task);
      set((state) => ({
        tasks: state.tasks.map((t) => t.id === taskId ? updatedTask : t)
      }));
      return updatedTask;
    },
    updateChunk: (taskId, chunkIndex, update) => {
      set((state) => ({
        tasks: state.tasks.map((t) => t.id === taskId ? {
          ...t,
          chunks: t.chunks.map((c, i) => i === chunkIndex ? update(c) : c)
        } : t)
      }));
    },
    completeTask: (taskId) => {
      const task = get().tasks.find((t) => t.id === taskId);
      if (!task || task.isCompleting) return;
      const result = task.result;
      if (!result) {
        get().failTask(taskId, new UploadError(UploadErrorCode.TaskMissingResult));
        return;
      }
      const newTask = {
        ...task,
        status: UploadTaskStatus.Completed,
        endTime: Date.now(),
        // If the file is stored, we release the file from the store to avoid memory leaks
        data: task.data.type === "file" && task.data.available ? {
          type: "file",
          available: false,
          file: {
            name: task.data.file.name,
            size: task.data.file.size,
            mimeType: task.data.file.type
          }
        } : task.data,
        error: void 0,
        progress: 100,
        isCompleting: false
      };
      if (task.complete) {
        set((state) => ({
          tasks: state.tasks.map((t) => t.id === taskId ? {
            ...t,
            isCompleting: true
          } : t)
        }));
        task.complete(task).then(() => {
          set((state) => ({
            tasks: state.tasks.map((t) => t.id === taskId ? newTask : t)
          }));
          newTask.resolve?.(result);
        }).catch((e) => {
          get().failTask(taskId, e);
        });
      } else {
        set((state) => ({
          tasks: state.tasks.map((t) => t.id === taskId ? newTask : t)
        }));
        newTask.resolve?.(result);
      }
      get().schedule();
    },
    failTask: (taskId, error) => {
      set((state) => ({
        tasks: state.tasks.map((t) => {
          if (t.id !== taskId) return t;
          if (t.reject) {
            t.reject(error);
          }
          return {
            ...t,
            status: UploadTaskStatus.Failed,
            error,
            progress: 0,
            endTime: Date.now(),
            // currently retry logic is not implemented in this store,
            // so we also cleanup the data from the store
            data: t.data.type === "file" && t.data.available ? {
              type: "file",
              available: false,
              file: {
                name: t.data.file.name,
                size: t.data.file.size,
                mimeType: t.data.file.type
              }
            } : t.data
          };
        })
      }));
      get().schedule();
    },
    removeTask: (taskId) => {
      set((state) => ({
        tasks: state.tasks.filter((task) => task.id !== taskId)
      }));
      get().schedule();
    },
    retryTask: (taskId) => {
      set((state) => ({
        tasks: state.tasks.map((task) => task.id === taskId ? {
          ...task,
          status: UploadTaskStatus.Pending
        } : task)
      }));
      get().schedule();
    },
    completeChunk: (taskId, chunkIndex, extra) => {
      set((state) => ({
        tasks: state.tasks.map((t) => t.id === taskId ? {
          ...t,
          chunks: t.chunks.map((c, i) => i === chunkIndex ? {
            ...c,
            status: UploadTaskStatus.Completed,
            progress: 100,
            ...extra
          } : c)
        } : t)
      }));
      get().schedule();
    },
    failChunk: (taskId, chunkIndex, error) => {
      set((state) => ({
        tasks: state.tasks.map((t) => t.id === taskId ? {
          ...t,
          chunks: t.chunks.map((c, i) => i === chunkIndex ? {
            ...c,
            status: UploadTaskStatus.Failed,
            retries: c.retries + 1,
            error,
            progress: 0
          } : c)
        } : t)
      }));
      get().schedule();
    },
    reset: () => {
      set(() => ({
        tasks: [],
        activeUploads: 0,
        activeChunks: 0
      }));
    }
  };
}));
const uploadLogger = createLogger("UPLOAD");
const logUploadTaskEvent = (taskId, message) => {
  uploadStore.getState().updateTask(taskId, (task) => ({
    ...task,
    events: [
      ...task.events,
      {
        taskId,
        message,
        timestamp: Date.now(),
        type: "log"
      }
    ]
  }));
  if (getCompatEnv().isFabricDev) {
    uploadLogger.log(message);
  }
};
const logUploadTaskError = (taskId, error, prefix = "") => {
  uploadStore.getState().updateTask(taskId, (task) => ({
    ...task,
    events: [
      ...task.events,
      {
        taskId,
        message: `${prefix} ${error.message}`,
        timestamp: Date.now(),
        type: "error"
      }
    ]
  }));
  if (getCompatEnv().isFabricDev) {
    uploadLogger.error(`${prefix} ${error.message}`, error);
  }
};

export { logUploadTaskError, logUploadTaskEvent, uploadStore };
//# sourceMappingURL=upload.store.js.map
